import { DOCUMENT, inject, Injectable } from '@angular/core'
import { Meta, MetaDefinition, Title } from '@angular/platform-browser'
import { ActivationEnd, Router } from '@angular/router'
import { createObject, DEFAULT_LANGUAGE } from '@pai/commons'
import { filter, Observable, ReplaySubject } from 'rxjs'

import { RouteDataKey } from 'app/static/route-data-key.enum'
import { optimizedCmsImageUrl } from 'app/utils/cms-image-utils'
import { AltUrl, AltUrls, createAltUrls } from 'app/utils/create-alternate-url.function'
import { filterIfInstanceOf } from 'app/utils/rxjs/filter-if-instance-of.operator'

import { CLIENT_CONFIG } from '../client-config/client-config.token'
import { SiteDto } from '../cms/type-unions'
import { CURRENT_LANG } from '../i18n/language.token'
import { JsonLd, JsonLdService } from '../json-ld.service'
import { LoggerService } from '../logger/logger.service'
import { ORIGIN } from '../origin/origin.token'
import { RouteMetaData } from './route-meta-data.type'
import { SocialShareData } from './social-share-data.type'

const PATHS_IGNORED_FOR_SITEMAP: string[] = []

const SOCIAL_SHARE_DATA_TAGS: Record<keyof SocialShareData, MetaDefinition[]> = {
  title: [
    { name: 'title' },
    { property: 'og:title' },
    { property: 'og:image:alt' },
    { name: 'twitter:title' },
    { name: 'twitter:image:alt' },
  ],
  description: [{ name: 'description' }, { property: 'og:description' }, { name: 'twitter:description' }],
  url: [{ property: 'og:url' }],
  image: [{ name: 'twitter:image' }, { property: 'og:image' }],
  published: [{ name: 'article:published_time' }, { name: 'publication_date' }],
  modified: [{ name: 'article:modified_time' }, { property: 'og:updated_time' }],
  author: [{ name: 'article:author' }, { name: 'author' }],
  section: [{ name: 'article:section' }],
  type: [{ property: 'og:type' }],
}

@Injectable()
export class MetaService {
  readonly altUrls$: Observable<AltUrls>

  private defaultJsonLd: JsonLd[] = []
  private readonly altUrlsSubject = new ReplaySubject<AltUrls>(1)
  private readonly logger = inject(LoggerService).getInstance('MetaService')
  private readonly doc: Document = inject(DOCUMENT)
  private readonly origin: string = inject(ORIGIN)
  private readonly currentLanguage = inject(CURRENT_LANG)
  private readonly jsonLdService = inject(JsonLdService)
  private readonly meta = inject(Meta)
  private readonly title = inject(Title)
  private readonly router = inject(Router)
  private readonly clientConfig = inject(CLIENT_CONFIG)

  constructor() {
    this.altUrls$ = this.altUrlsSubject.asObservable()

    // set document language
    this.doc.documentElement.setAttribute('lang', this.currentLanguage)

    this.router.events
      .pipe(
        filterIfInstanceOf(ActivationEnd),
        // no children = leaf route
        filter((ev) => ev.snapshot.children.length === 0),
      )
      .subscribe(this.onRouteActivationEnd)

    // set noindex meta tag on none prod envs
    if (!this.clientConfig?.isProd) {
      this.setNoIndex()
    }
  }

  setDefaultJsonLd(jsonLd: JsonLd[]) {
    this.defaultJsonLd = jsonLd
  }

  /**
   * replace all current Data with the provided breadcrumb items
   * (transformed into jsonLd BreadcrumbList) and the defaultJsonLd data
   */
  setBreadcrumbList(items: Array<{ item: string; name: string; position: number }>) {
    // set json-ld breadcrumb metadata
    const listItems: JsonLd[] = items.map((item) => createObject('ListItem', item))
    const breadcrumbList = createObject('BreadcrumbList', { itemListElement: listItems })
    this.jsonLdService.setData([...this.defaultJsonLd, breadcrumbList])
  }

  private readonly onRouteActivationEnd = ({ snapshot }: ActivationEnd) => {
    this.logger.debug('onRouteActivationEnd')
    const routeMetaData: RouteMetaData = snapshot.data[RouteDataKey.META]
    const routePageData: SiteDto | undefined = snapshot.data[RouteDataKey.SITE]

    const p0 = snapshot.url[0]?.path ?? ''
    if (routeMetaData.noIndex || PATHS_IGNORED_FOR_SITEMAP.includes(p0)) {
      this.setNoIndex()
    }

    if (routeMetaData.seoRetrievalFn) {
      const seoData = routeMetaData.seoRetrievalFn(snapshot)
      this.title.setTitle(seoData.title)
      this.setMetaData(seoData)
    }

    if (routePageData) {
      const altUrls = createAltUrls(this.origin, snapshot, routePageData)
      this.altUrlsSubject.next(altUrls)

      Object.values(altUrls).forEach(({ lang, altUrl }: AltUrl) => this.updateLangHrefLink(lang, altUrl, 'alternate'))
      this.updateLangHrefLink(this.currentLanguage, altUrls[this.currentLanguage].canonicalUrl, 'canonical')
      this.updateLangHrefLink('x-default', altUrls[DEFAULT_LANGUAGE].altUrl, 'alternate')
    }
  }

  private setNoIndex() {
    this.meta.addTag({ name: 'robots', content: 'noindex' })
  }

  private setMetaData(socialShareData: SocialShareData) {
    let image: string | null = null
    let width = 1200
    let height = 630
    const targetRatio = width / height

    if (socialShareData.image && socialShareData.image.width && socialShareData.image.height) {
      width = Math.min(width, socialShareData.image.width)
      // align with effective max usable width while keeping the target ratio
      height = Math.round(width / targetRatio)

      // if height exceeds the max usable height, adjust using height as the constraint
      const maxUsableH = Math.min(height, socialShareData.image.height)
      if (height > maxUsableH) {
        height = maxUsableH
        width = Math.round(height * targetRatio)
      }

      image = optimizedCmsImageUrl(this.clientConfig.assetsOrigin, socialShareData.image, width, height, 85, 'jpeg')
    }

    const metaData = {
      ...socialShareData,
      image,
      url: `${this.origin}${this.router.url}`,
    }

    ;(<Array<[keyof SocialShareData, string | null]>>Object.entries(metaData)).forEach(([key, content]) => {
      if (content) {
        SOCIAL_SHARE_DATA_TAGS[key]
          .map((md) => ({ ...md, content }) satisfies MetaDefinition)
          .forEach((md) => this.meta.updateTag(md))
      } else {
        SOCIAL_SHARE_DATA_TAGS[key]
          .map((md) => (md.name ? `name='${md.name}'` : md.property ? `property='${md.property}'` : ''))
          .forEach((attrSelector) => this.meta.removeTag(attrSelector))
      }
    })
    // special
    if (image) {
      this.meta.updateTag({ property: 'og:image:width', content: String(width) })
      this.meta.updateTag({ property: 'og:image:height', content: String(height) })
    } else {
      this.meta.removeTag("property='og:image:width'")
      this.meta.removeTag("property='og:image:height'")
    }
  }

  private updateLangHrefLink(lang: string, href: string, type: 'alternate' | 'canonical') {
    const linkSelector = type === 'alternate' ? `link[rel="alternate"][hreflang="${lang}"]` : 'link[rel="canonical"]'
    const list: NodeList = this.doc.head.querySelectorAll(linkSelector)

    let el: HTMLLinkElement | undefined = <HTMLLinkElement>list[0]
    if (!el) {
      el = this.doc.createElement('link')
      el.setAttribute('rel', type)
      el.setAttribute('href', href)
      if (type === 'alternate') {
        el.setAttribute('hreflang', lang)
      }
      this.doc.head.appendChild(el)
    }
    el.setAttribute('href', href)
  }
}
